import React, { useState } from 'react';
import { useLogInspector } from './hooks/useLogInspector';
import { generateSampleData } from './utils/helpers';
import Header from './components/Header';
import Tabs from './components/Tabs';
import Toolbar from './components/Toolbar';
import LogViewer from './components/LogViewer';
import Bookmarks from './components/Bookmarks';
import ImportModal from './components/ImportModal';
import BookmarkModal from './components/BookmarkModal';
import LogDrawer from './components/LogDrawer';

function App() {
  const {
    rawLogs,
    containers,
    tabs,
    activeTabId,
    activeTab,
    bookmarks,
    showContainers,
    containerDropdownOpen,
    timestampMode,
    filteredLogs,
    processImport,
    processImportFiles,
    addTabFromCurrentFilter,
    closeTab,
    switchTab,
    updateFilter,
    toggleContainer,
    toggleAllContainers,
    toggleBookmark,
    isBookmarked,
    updateBookmarkNote,
    removeBookmark,
    setShowContainers,
    setContainerDropdownOpen,
    setTimestampMode
  } = useLogInspector();

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [bookmarkModalOpen, setBookmarkModalOpen] = useState(false);
  const [editingBookmark, setEditingBookmark] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleImport = async (text) => {
    setImportLoading(true);
    // Use setTimeout to allow UI to update with loading state
    await new Promise(resolve => setTimeout(resolve, 50));

    const count = processImport(text);
    if (count) {
      console.log(`${count} lines parsed.`);
    }

    setImportLoading(false);
    setImportModalOpen(false);
  };

  const handleImportFiles = async (logFiles) => {
    setImportLoading(true);
    // Use setTimeout to allow UI to update with loading state
    await new Promise(resolve => setTimeout(resolve, 50));

    const count = processImportFiles(logFiles);
    if (count) {
      console.log(`${count} lines parsed from ${logFiles.length} file(s).`);
    }

    setImportLoading(false);
    setImportModalOpen(false);
  };

  const handleDemoClick = async () => {
    setImportModalOpen(true);
    setImportLoading(true);
    // Small delay to ensure modal renders before processing
    await new Promise(resolve => setTimeout(resolve, 100));

    const count = processImport(generateSampleData());
    if (count) {
      console.log(`${count} lines parsed.`);
    }

    setImportLoading(false);
    setImportModalOpen(false);
  };

  const handleToggleBookmark = (logId) => {
    if (isBookmarked(logId)) {
      toggleBookmark(logId);
    } else {
      // Directly add bookmark without asking for a note
      toggleBookmark(logId, '');
    }
  };

  const handleSaveBookmark = (note) => {
    if (editingBookmark) {
      // Editing existing bookmark
      updateBookmarkNote(editingBookmark.logId, note);
      setEditingBookmark(null);
    }
  };

  const handleEditBookmark = (bookmark) => {
    setEditingBookmark(bookmark);
    setBookmarkModalOpen(true);
  };

  const handleJumpToLog = (logId) => {
    // First check if the log is in the filtered logs
    const logIndex = filteredLogs.findIndex(log => log.id === logId);

    if (logIndex === -1) {
      // Log is actually filtered out
      alert("The bookmarked line is currently hidden by your filters. Clear filters or switch tabs to view it.");
      return;
    }

    // Try to find the element - it might not exist yet if it's out of view in the virtual list
    const el = document.getElementById(`log-${logId}`);
    if (el) {
      // Element exists, scroll to it
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('highlighted');
      setTimeout(() => el.classList.remove('highlighted'), 2000);
    } else {
      // Element doesn't exist yet (virtualized), calculate and scroll to approximate position
      const scrollContainer = document.querySelector('.scrollbar-thin');
      if (scrollContainer) {
        const rowHeight = 24;
        const targetPosition = logIndex * rowHeight;
        scrollContainer.scrollTo({ top: targetPosition, behavior: 'smooth' });

        // Wait for scroll and virtualization, then highlight
        setTimeout(() => {
          const el = document.getElementById(`log-${logId}`);
          if (el) {
            el.classList.add('highlighted');
            setTimeout(() => el.classList.remove('highlighted'), 2000);
          }
        }, 500);
      }
    }
  };

  const handleLineClick = (log) => {
    setSelectedLog(log);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const statusText = rawLogs.length > 0
    ? `Showing ${filteredLogs.length} / ${rawLogs.length} events`
    : 'Ready';

  return (
    <div className="h-screen flex flex-col">
      <Header
        onDemoClick={handleDemoClick}
      />

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex flex-col min-w-0 bg-slate-900">
          <Tabs
            tabs={tabs}
            activeTabId={activeTabId}
            onTabSwitch={switchTab}
            onTabClose={closeTab}
            onCreateTabFromFilter={addTabFromCurrentFilter}
          />

          <Toolbar
            filterText={activeTab?.filters.text || ''}
            invertFilter={activeTab?.filters.invertText || false}
            onFilterChange={updateFilter}
            containers={containers}
            selectedContainers={activeTab?.filters.containers || []}
            onToggleContainer={toggleContainer}
            onToggleAllContainers={toggleAllContainers}
            showContainers={showContainers}
            onShowContainersChange={setShowContainers}
            timestampMode={timestampMode}
            onTimestampModeChange={setTimestampMode}
            dropdownOpen={containerDropdownOpen}
            onDropdownToggle={setContainerDropdownOpen}
            statusText={statusText}
          />

          <LogViewer
            logs={filteredLogs}
            hasLogs={rawLogs.length > 0}
            showContainers={showContainers}
            filterText={activeTab?.filters.text || ''}
            timestampMode={timestampMode}
            isBookmarked={isBookmarked}
            onToggleBookmark={handleToggleBookmark}
            bookmarks={bookmarks}
            onJumpToLog={handleJumpToLog}
            onLineClick={handleLineClick}
            onImportClick={() => setImportModalOpen(true)}
          />
        </div>

        <Bookmarks
          bookmarks={bookmarks}
          onJumpToLog={handleJumpToLog}
          onEditBookmark={handleEditBookmark}
          onRemoveBookmark={removeBookmark}
          filteredLogs={filteredLogs}
        />
      </div>

      <ImportModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImport={handleImport}
        onImportFiles={handleImportFiles}
        isLoading={importLoading}
      />

      <BookmarkModal
        isOpen={bookmarkModalOpen}
        onClose={() => {
          setBookmarkModalOpen(false);
          setEditingBookmark(null);
        }}
        onSave={handleSaveBookmark}
        initialNote={editingBookmark?.note || ''}
      />

      <LogDrawer
        log={selectedLog}
        isOpen={drawerOpen}
        onClose={handleDrawerClose}
        onToggle={handleDrawerToggle}
        isBookmarked={selectedLog ? isBookmarked(selectedLog.id) : false}
        onToggleBookmark={handleToggleBookmark}
        timestampMode={timestampMode}
      />
    </div>
  );
}

export default App;

